import clsx from 'clsx';
import { tlwTwMerge } from '@trg/utils';
import { Link, type LinkProps } from '@trg/tlw-ui-component';
import { useLayoutStore } from '../../../layout';
import { useProductCardContext } from './ProductCardContext';
import { FavoriteButton } from './FavoriteButton';
import { AddToCartButton } from './AddToCartButton';
import { ProductCardPreview } from './ProductCardPreview';
import { ProductCardSpecs } from './ProductCardSpecs';
import { ProductCardTags } from './ProductCardTags';
import { PriceDetail } from '../PriceDetail';

export function ProductCardForDefault({
  className,
  children,
  ...props
}: LinkProps) {
  const isDesktop = useLayoutStore.use.isDesktop();
  const {
    product,
    showFavorite,
    squarePreview,
    small,
    swatches,
    currentSwatch,
    isVertical,
    isHorizontal,
    generateDataset,
  } = useProductCardContext();

  const hasSwatches = swatches.length > 1;
  const isTagsVisible = isVertical && Boolean(product.otherTag?.length);

  return (
    <Link
      className={tlwTwMerge(
        'tlw-group/product-card tlw-card-shadow-product tlw-grid tlw-bg-white tlw-rounded-lg tlw-overflow-hidden',
        isVertical && 'tlw-grid-rows-[auto_1fr]',
        isHorizontal && 'tlw-grid-cols-[auto_1fr] tlw-auto-rows-[100%]',
        className
      )}
      href={currentSwatch.link}
      {...props}
      {...generateDataset()}
    >
      {/* Preview 區域 */}
      <div
        className={clsx(
          'tlw-relative tlw-max-w-full tlw-max-h-full tlw-bg-gray-200 tlw-overflow-hidden',
          squarePreview ? 'tlw-aspect-square' : 'tlw-aspect-auto'
        )}
      >
        {/* 商品圖 */}
        <ProductCardPreview />

        {/* 收藏按鈕 */}
        {showFavorite && (
          <FavoriteButton className="tlw-z-1 tlw-absolute tlw-top-0 tlw-right-0" />
        )}

        {/* 購物車按鈕 （出现场景：桌面端竖版卡片） */}
        {isDesktop && isVertical && (
          <AddToCartButton className="tlw-z-1 tlw-absolute tlw-bottom-2.5 tlw-right-2.5" />
        )}
      </div>

      {/* Introduction 區域 */}
      <div
        className={clsx(
          'tlw-relative tlw-flex tlw-flex-col tlw-gap-2 tlw-overflow-hidden',
          isVertical && ['tlw-p-2 lg:tlw-gap-4', !small && 'lg:tlw-p-4'],
          isHorizontal && 'tlw-px-2 tlw-py-1.5 lg:tlw-px-4 lg:tlw-py-3'
        )}
      >
        <div className={clsx('tlw-space-y-1', !small && 'lg:tlw-space-y-2')}>
          {/* 商品名 */}
          <p className="tlw-txt-zh-head-7 tlw-text-txt-700 tlw-line-clamp-2">
            {currentSwatch.name}
          </p>

          {/* 商品規格 */}
          {hasSwatches ? <ProductCardSpecs /> : <div className="tlw-h-4" />}
        </div>

        <div
          className={clsx('tlw-flex-1 tlw-flex tlw-flex-col tlw-gap-2', {
            'tlw-justify-end': isVertical,
            'lg:tlw-justify-end': isHorizontal,
          })}
        >
          {/* 商品價格 */}
          <PriceDetail
            listPrice={product.listPrice}
            salePrice={product.salePrice}
            pointPrice={product.pointPrice}
            pointPriceName={product.pointPriceName}
            overturn={isVertical}
          />

          {/* 商品標簽 */}
          {isTagsVisible && <ProductCardTags />}
        </div>

        {/* 購物車按鈕（出现场景：1.横板卡片、2.非桌面端竖版卡片） */}
        {(isHorizontal || (!isDesktop && isVertical)) && (
          <AddToCartButton
            className={clsx({
              'tlw-self-end tlw-mx-1': isVertical,
              'tlw-absolute tlw-bottom-2 tlw-right-2 lg:tlw-bottom-2.5 lg:tlw-right-2.5':
                isHorizontal,
            })}
          />
        )}
      </div>
    </Link>
  );
}
